import React, { useMemo, useState } from "react";
import {
	Autocomplete,
	Avatar,
	Box,
	Chip,
	CircularProgress,
	debounce,
	TextField,
	Typography,
} from "@mui/material";
import { imgUrl } from "src/Helpers/utils/constants";
import { useGetQueryWithTotal } from "src/hooks/useGetQueryWithTotal";
import type { IB2BMemberOption } from "src/Types/module/b2bSession/b2bSessionRequest.types";

interface Props {
	label: string;
	placeholder?: string;
	value?: number | null;
	initialOption?: Partial<IB2BMemberOption> | null;
	onChange: (b2bId: number | null, member?: IB2BMemberOption | null) => void;
	error?: boolean;
	helperText?: string;
	required?: boolean;
	disabledId?: number | null; // e.g. avoid selecting same from and to B2B
}

const B2BSearchSelect: React.FC<Props> = ({
	label,
	placeholder = "Search B2B by name, company, email...",
	value,
	initialOption,
	onChange,
	error,
	helperText,
	required,
	disabledId,
}) => {
	const [querySearch, setQuerySearch] = useState("");

	const debouncedSearch = useMemo(
		() =>
			debounce((val: string) => {
				setQuerySearch(val.trim());
			}, 350),
		[]
	);

	const handleInputChange = (_: any, newInputValue: string) => {
		debouncedSearch(newInputValue);
	};

	const { data, isLoading } = useGetQueryWithTotal<IB2BMemberOption[]>({
		endPoint: "/api/fair/admin/b2b",
		queryKeys: ["B2B", querySearch],
		params: {
			name: querySearch,
		},
	});

	const options: IB2BMemberOption[] = useMemo(() => {
		const list: IB2BMemberOption[] = Array.isArray(data?.data)
			? [...data.data]
			: [];

		if (initialOption && initialOption.b2b_id) {
			const exists = list.some((opt) => opt.b2b_id === initialOption.b2b_id);
			if (!exists) {
				list.unshift(initialOption as IB2BMemberOption);
			}
		}

		return list;
	}, [data, initialOption]);

	// Selected member object if available
	const selectedMember = useMemo(() => {
		if (!value) return null;
		return options.find((opt) => opt.b2b_id === value) || null;
	}, [options, value]);

	return (
		<Box>
			<Autocomplete<IB2BMemberOption, false, false, false>
				options={options}
				value={selectedMember}
				loading={isLoading}
				getOptionLabel={(option: string | IB2BMemberOption) => {
					if (!option) return "";
					if (typeof option === "string") return option;
					const name = `${option.b2b_first_name || ""} ${
						option.b2b_last_name || ""
					}`.trim();
					return name || option.b2b_organization || option.b2b_email || "N/A";
				}}
				isOptionEqualToValue={(option, val) => {
					if (!option || !val) return false;
					return option.b2b_id === val.b2b_id;
				}}
				getOptionDisabled={(option) => {
					if (!option || typeof option === "string") return false;
					return disabledId ? option.b2b_id === disabledId : false;
				}}
				onInputChange={handleInputChange}
				onChange={(_, selected) => {
					if (selected && typeof selected !== "string") {
						onChange(selected.b2b_id, selected);
					} else {
						onChange(null, null);
					}
				}}
				renderOption={(props, option) => {
					if (typeof option === "string") {
						return (
							<Box component="li" {...props} key={option}>
								{option}
							</Box>
						);
					}
					const fullName = `${option.b2b_first_name || ""} ${
						option.b2b_last_name || ""
					}`.trim();
					const avatarSrc = option.b2b_photo
						? `${imgUrl}/${option.b2b_photo}`
						: undefined;

					const subInfo = option.b2b_organization
						? `${option.b2b_organization}${
								option.b2b_designation ? ` • ${option.b2b_designation}` : ""
						  }`
						: option.b2b_email || option.b2b_phone || "";

					return (
						<Box
							component="li"
							{...props}
							key={option.b2b_id}
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1.5,
								py: 1,
								px: 1.5,
								borderBottom: "1px solid rgba(0,0,0,0.04)",
							}}
						>
							<Avatar
								src={avatarSrc}
								alt={fullName}
								sx={{
									width: 34,
									height: 34,
									fontSize: "0.85rem",
									bgcolor: "primary.main",
								}}
							>
								{fullName ? fullName.charAt(0).toUpperCase() : "B"}
							</Avatar>
							<Box flex={1} minWidth={0}>
								<Box display="flex" alignItems="center" gap={1}>
									<Typography variant="body2" fontWeight={600} noWrap>
										{fullName || "N/A"}
									</Typography>
									{option.b2b_type && (
										<Chip
											label={option.b2b_type}
											size="small"
											sx={{
												height: 18,
												fontSize: "0.68rem",
												fontWeight: 700,
												bgcolor:
													option.b2b_type.toLowerCase() === "buyer"
														? "rgba(14, 165, 233, 0.12)"
														: "rgba(16, 185, 129, 0.12)",
												color:
													option.b2b_type.toLowerCase() === "buyer"
														? "#0284C7"
														: "#059669",
											}}
										/>
									)}
								</Box>
								{subInfo && (
									<Typography
										variant="caption"
										color="text.secondary"
										noWrap
										display="block"
									>
										{subInfo}
									</Typography>
								)}
							</Box>
						</Box>
					);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						placeholder={placeholder}
						size="small"
						required={required}
						error={error}
						helperText={helperText}
						InputLabelProps={{ shrink: true }}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={18} />
									) : null}
									{params.InputProps.endAdornment}
								</>
							),
						}}
					/>
				)}
			/>
		</Box>
	);
};

export default B2BSearchSelect;
